//
// twitter-typeahead.js
//
// Copyright (c) 2016 by Curalate, Inc.
//

/*global angular: false, _: false */

(function() {
    "use strict";

    var DEFAULT_MIN_SEARCH_LENGTH = 5,
        keyCodeMap = {
            9: "tab",
            27: "esc",
            37: "left",
            39: "right",
            13: "enter",
            38: "up",
            40: "down"
        };

    /**
     * twitterTypeahead
     *
     * Comprehensive wrapper directive around Twitter's jQuery typeahead plugin
     *
     * Scope:
     *  getData:    An Angular expression which returns a Promise which resolves to an array of "results"
     *              Note: The typeahead keeps a reference to the current set of results, and if the expression evaluates to "undefined", it will use those results
     *              This was done in an effort to support "debouncing" and/or "throttling" of requests
     *  templates:  An object which contains information for how to retrieve/render templates.
     *              The template object uses template-name as the key ["header", "footer", "empty", "suggestion"]. These keys point to another object
     *              which contains the template information. For instance: { "url": "/path/to/html/template" } or { "html" : "<div>{{ arbitrary Angular expression }}</div>" }
     *              Example:
     *                { suggestion: { url: "/partial/url" }, footer: { html: "<div>{{ expression }}</div>" }
     *  key:        String or Function describing the value of a particular expression. See "displayKey" here: https://github.com/twitter/typeahead.js/blob/master/doc/jquery_typeahead.md#datasets
     *  context:    Object containing values that will be accessible in the scope in which the "suggestion" template is rendered
     *              This is used to pass arbitrary values or out-of-scope functions into the template's isolate scope
     *              A particular example would be to pass a function used as an ng-click callback. See locations.js for an example of this
     */
    angular.module('curalate').
        constant('twitterTypeahead.templateDefaults', {
           header: '',
           footer: '',
           empty: '',
           suggestion: '<p>{{ name }}</p>'
        }).
        directive('twitterTypeahead', ['$rootScope', '$compile', '$interpolate', '$timeout', '$http', '$q', 'twitterTypeahead.templateDefaults', 'TrackingService',
        function($rootScope, $compile, $interpolate, $timeout, $http, $q, templateDefaults, TrackingService) {
            return {
                restrict: 'AC',
                transclude: true,
                scope: {
                    getData: '=',
                    templates: '=',
                    key: '=',
                    events: '=?',
                    context: '=',
                    trackCategory: '@'
                },
                link: function(scope, element, attrs) {
                    var minLength = attrs.minLength || DEFAULT_MIN_SEARCH_LENGTH;
                    var bindTypeahead = function(templates) {
                        var compiledTemplates,
                            linkedTemplates = {},
                            lastArray = [],
                            rendered = {},
                            generateHtmlFunc,
                            lastQuery,
                            lastCursorItem;

                        // Combine default templates with ones passed into the function,
                        // compile all of them and return an map
                        compiledTemplates = _.chain(templateDefaults).
                            map(function(value, key) {
                                var obj = {}, existing = _(templates).findWhere({ type: key });
                                obj[key] = (existing && existing.template) ? $compile(existing.template) : $compile(value);
                                return obj;
                            }).
                            reduce(function(memo, value) { return _.extend(memo, value); }, {}).
                            value();

                        generateHtmlFunc = function(where) {
                            var ele = angular.element(where);

                            // Return DOM if we have it in order to preserve events
                            return ele.length > 0 ? ele[0] : '';
                        };

                        var typeaheadElem = angular.element(element).typeahead({
                            minLength: minLength,
                            highlight: true
                        }, {
                            // Reimplement this crap as a Bloodhound dataset
                            source: function(query, cb, asynccb) {
                                var deferred = $q.defer();

                                lastQuery = query;

                                // Call the custom "getData" function and pass the deferred to be resolved
                                scope.getData(query, deferred);

                                // If the specified returns undefined, the last set of results is used
                                // This logic covers the case of callbacks which are wrapped in _.debounce or _.throttle calls
                                deferred.promise.then(function(results) {
                                    // Only hit TrackingService if the custom getData function resolved the deferred we passed
                                    TrackingService.recordEvent(scope.trackCategory, 'TypeaheadSearch', query);

                                    lastArray = results;

                                    // Destroy old scopes from previously rendered items
                                    _.chain(rendered).
                                        map(function(dom) { return angular.element(dom).scope(); }).
                                        compact().
                                        each(function(scope) { scope.$destroy(); });

                                    rendered = {};

                                    // Update scope variables with current state so templates have access to them
                                    scope.query = query;
                                    scope.isEmpty = results.length === 0;
                                    scope.resultTotal = results.length;

                                    // Re-link the three "static" templates with the new state
                                    linkedTemplates.header = compiledTemplates.header(scope, angular.noop);
                                    linkedTemplates.footer = compiledTemplates.footer(scope, angular.noop);
                                    linkedTemplates.empty = compiledTemplates.empty(scope, angular.noop);

                                    // Link each result against the template
                                    if (results.length > 0) {
                                        _(results).each(function(result, i) {
                                            var newScope = scope.$new(true);
                                            result._scope_id = newScope.$id;
                                            newScope.data = result;
                                            newScope.data.index = i;
                                            newScope.context = scope.context;
                                            newScope.isEmpty = scope.isEmpty;
                                            newScope.query = query;

                                            // Pass the noop function to get element cloning only
                                            rendered[result._scope_id] = compiledTemplates.suggestion(newScope, angular.noop);
                                        });
                                    }

                                    // Wait until the current $digest loop finishes to call the typeahead callback
                                    // so the data bindings for the templates have a chance to update
                                    $timeout(function() {
                                        if (_.isFunction(asynccb)) {
                                            asynccb(results);
                                        } else {
                                            cb(results);
                                        }
                                    });
                                });
                            },
                            displayKey: function(obj) {
                                if (angular.isFunction(scope.key)) {
                                    return scope.key.call(this, obj);
                                } else {
                                    return obj[scope.key];
                                }
                            },
                            templates: {
                                header: function() {
                                    return generateHtmlFunc(linkedTemplates.header);
                                },
                                footer: function() {
                                    return generateHtmlFunc(linkedTemplates.footer);
                                },
                                empty: function() {
                                    return generateHtmlFunc(linkedTemplates.empty);
                                },
                                suggestion: function(obj) {
                                    return generateHtmlFunc(rendered[obj._scope_id]);
                                }
                            }
                        });

                        // Tie into typeahead custom events
                        scope.events = scope.events || {};

                        _.chain(scope.events).keys().each(function(key) {
                            typeaheadElem.on('typeahead:' + key, function(e) {
                                var args = arguments;
                                scope.$apply(function() {
                                    scope.events[key].apply(typeaheadElem, [].slice.call(args, 0));
                                    TrackingService.recordEvent(scope.trackCategory, 'TypeaheadInteraction', key);
                                });
                            });
                        });

                        // Keep track to item cursor points to in order to enrich DOM events below
                        // NOTE: cursor change events are only fired for keyboard navigation
                        typeaheadElem.on('typeahead:cursorchanged', function(e, rawSelection) {
                            TrackingService.recordEvent(scope.trackCategory, 'TypeaheadInteraction', 'cursorchanged');
                            lastCursorItem = rawSelection;
                        });

                        // Tie into typeahead DOM events
                        typeaheadElem.on('keydown.tt', function(e) {
                            var key = keyCodeMap[e.which || e.keyCode];

                            if (key && scope.events[key + 'Keyed']) {
                                scope.$apply(function() {
                                    scope.events[key + 'Keyed'].call(typeaheadElem, e, lastCursorItem, lastQuery);
                                    TrackingService.recordEvent(scope.trackCategory, 'TypeaheadInteraction', key);
                                });
                            }
                        });
                    };

                    scope.$on('twitter-typeahead:reset', function() {
                        var typeaheadElem = angular.element(element);
                        typeaheadElem.typeahead('val', null);
                        typeaheadElem.typeahead('close');
                    });

                    // Create a list of promises, one for each template
                    // If the template is just a string, create a pre-resolved promise
                    // If the template is a url, request it and return the promise
                    var promises = _(scope.templates).map(function(obj, key) {
                        var deferred;

                        if (obj.url) {
                            deferred = $q.defer();
                            $http.get(obj.url).then(function(result) {
                                deferred.resolve({
                                    type: key,
                                    template: result.data
                                });
                            });

                            return deferred.promise;
                        } else {
                            return $q.when({
                                type: key,
                                template: obj.html
                            });
                        }
                    });

                    // Combine the list of promise in a single promise and bind the typeahead
                    // when all promises have returned
                    $q.all(promises).then(bindTypeahead);
                }
            };
    }]);
})();
